import { useEffect, useRef, useState } from "react";
import { FileText, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Thumb, openFile } from "@/components/Customers";

function ScansDialog({ inv, open, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get(`/invoices/${inv.id}/scans`).then((r) => setData(r.data)).catch((e) => { toast.error(formatError(e)); onClose(); });
  }, [open, inv.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-card" data-testid="invoice-scans-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Scans for invoice {inv.number}</DialogTitle>
          <DialogDescription>{inv.customer?.name} · scans in the customer folder with this invoice #</DialogDescription>
        </DialogHeader>
        {!data ? <Loader2 className="animate-spin text-primary" /> : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="invoice-scans-grid">
            {data.files.map((f) => (
              <div key={f.id} className="space-y-1"><Thumb file={f} onOpen={openFile} /><p className="text-[11px] truncate font-mono">{f.original_filename}</p></div>
            ))}
            {!data.files.length && <p className="col-span-full text-sm text-muted-foreground">No scans yet.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function InvoiceScansCell({ inv, i, count, onAdded }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const input = useRef(null);
  const upload = async (files) => {
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      const form = new FormData();
      form.append("file", f, f.name);
      try { await api.post(`/invoices/${inv.id}/scans`, form); ok += 1; } catch (e) { toast.error(`${f.name}: ${formatError(e)}`); break; }
    }
    if (ok) toast.success(`${ok} scan${ok === 1 ? "" : "s"} added to invoice ${inv.number} (${inv.customer?.name}'s folder)`);
    setBusy(false);
    onAdded();
  };
  return (
    <div className="flex items-center gap-1.5">
      <button onClick={() => count && setOpen(true)} disabled={!count} data-testid={`invoice-scans-${i}`} title={count ? "View scans" : "No scans yet"}
        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-mono transition-colors ${count ? "bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25" : "text-muted-foreground"}`}>
        <FileText size={12} /> {count ?? "–"}
      </button>
      <input ref={input} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid={`invoice-scan-input-${i}`} />
      <button onClick={() => input.current.click()} disabled={busy} className="text-muted-foreground hover:text-primary" data-testid={`invoice-add-scan-${i}`} title="Add scan" aria-label={`Add scan to invoice ${inv.number}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
      </button>
      {open && <ScansDialog inv={inv} open={open} onClose={() => setOpen(false)} />}
    </div>
  );
}
